import type { CSSProperties } from 'react';

/**
 * Ícones em SVG pixel art bem desenhados (12×12).
 * Estilo retrô NES/GBA: silhueta clara, contorno preto, 1-2 highlights.
 */

export type PixelIconName =
  | 'feed' | 'play' | 'clean' | 'discipline' | 'heal' | 'minigame' | 'light' | 'lightOff'
  | 'globe' | 'settings' | 'book' | 'dna' | 'tree' | 'reset'
  | 'house' | 'world' | 'sword' | 'shield' | 'heart' | 'inventory'
  | 'berry' | 'shell' | 'crystal' | 'flower'
  | 'sun' | 'moon' | 'skull' | 'sleeping';

interface Props {
  name: PixelIconName;
  size?: number;
  color?: string;
  style?: CSSProperties;
}

interface IconDef {
  /** Width × Height (sempre quadrado 12). */
  pixels: ReadonlyArray<readonly [number, number, string]>;
}

// Cores nominais — substituídas pela `color` prop quando = '#main'
const C = {
  main: '#main',     // será substituído pela cor da prop
  k: '#1a1a14',      // contorno escuro
  l: '#fff8e0',      // highlight branco-creme
  w: '#ffffff',
  red: '#e64a4a',
  pink: '#ff8db1',
  orange: '#f08c3a',
  yellow: '#f0c870',
  green: '#5fbb5a',
  greenDark: '#2f7a3a',
  blue: '#5aafff',
  cyan: '#4ad4ff',
  purple: '#9a5ed1',
  brown: '#8b5a2f',
  brownDark: '#5a3818',
  grey: '#aaaaaa',
} as const;

// Helper para construir grid (cada linha = string de 12 chars onde cada char é a chave do mapa)
function pxFromStr(rows: string[], map: Record<string, string>): IconDef['pixels'] {
  const out: Array<[number, number, string]> = [];
  rows.forEach((row, y) => {
    [...row].forEach((ch, x) => {
      if (ch === '.' || ch === ' ') return;
      const c = map[ch];
      if (c) out.push([x, y, c]);
    });
  });
  return out;
}

const ICONS: Record<PixelIconName, IconDef> = {
  // ===== AÇÕES DA CASA =====
  feed: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '..KKYYYYKK..',
        '.KYYYYYYYYK.',
        '.KYYDDYYDYK.',
        '.KYYYYYYYYK.',
        '.KYYDYYDDYK.',
        '.KYYYYYYYYK.',
        '..KYYYYYYK..',
        '...KKKKKK...',
        '............',
        '............',
      ],
      { K: C.k, Y: C.yellow, D: C.brown },
    ),
  },
  play: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '...KCCCCK...',
        '..KCCCCCCK..',
        '..KCCWWCCK..',
        '..KCWWWWCK..',
        '..KCCWWCCK..',
        '..KCCCCCCK..',
        '...KCCCCK...',
        '....KKKK....',
        '............',
        '............',
      ],
      { K: C.k, C: C.cyan, W: C.w },
    ),
  },
  clean: {
    pixels: pxFromStr(
      [
        '............',
        '......KK....',
        '.....KOOK...',
        '....KOOK....',
        '...KOOK.....',
        '..KOOK......',
        '.KYYYK......',
        'KYYYYYK.....',
        'KYYYYYK.....',
        '.KKKKKK.....',
        '............',
        '............',
      ],
      { K: C.k, O: C.brown, Y: C.yellow },
    ),
  },
  discipline: {
    pixels: pxFromStr(
      [
        '....KK......',
        '...KLLK.....',
        '...KLLK.....',
        '...KLLK.KK..',
        '...KLLKKLLK.',
        '.KKKLLLLLLK.',
        'KLLKLLLLLLK.',
        'KLLLLLLLLK..',
        'KLLLLLLLK...',
        '.KLLLLLLK...',
        '..KLLLLK....',
        '...KKKK.....',
      ],
      { K: C.k, L: '#ffe0c0' },
    ),
  },
  heal: {
    pixels: pxFromStr(
      [
        '............',
        '...KKKKKK...',
        '..KCCCCCCK..',
        '.KCCCWWCCCK.',
        '.KCCCWWCCCK.',
        '.KKKKKKKKKK.',
        '.KRRRRRRRRK.',
        '.KRRRWWRRRK.',
        '.KRRRWWRRRK.',
        '..KRRRRRRK..',
        '...KKKKKK...',
        '............',
      ],
      { K: C.k, C: C.cyan, R: C.red, W: C.w },
    ),
  },
  minigame: {
    pixels: pxFromStr(
      [
        '............',
        '...KKKKKK...',
        '..KLLLLLLK..',
        '.KLKLLLLKLK.',
        '.KLLLLLLLLK.',
        '.KLLKKLLKLK.',
        '.KLKKLLLLLK.',
        '.KLLLLLKKLK.',
        '.KLKKLLKKLK.',
        '..KLLLLLLK..',
        '...KKKKKK...',
        '............',
      ],
      { K: C.k, L: C.l },
    ),
  },
  light: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '...KYYYYK...',
        '..KYWWYYYK..',
        '..KYWYYYYK..',
        '..KYYYYYYK..',
        '..KYYYYYYK..',
        '...KYYYYK...',
        '....KKKK....',
        '....KMMK....',
        '....KMMK....',
        '.....KK.....',
      ],
      { K: C.k, Y: C.yellow, W: C.w, M: C.main },
    ),
  },
  lightOff: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '...KGGGGK...',
        '..KGGGGGGK..',
        '..KGGGGGGK..',
        '..KGGGGGGK..',
        '..KGGGGGGK..',
        '...KGGGGK...',
        '....KKKK....',
        '....KMMK....',
        '....KMMK....',
        '.....KK.....',
      ],
      { K: C.k, G: '#5a5a6a', M: C.main },
    ),
  },

  // ===== TOPBAR =====
  globe: {
    pixels: pxFromStr(
      [
        '............',
        '...KKKKKK...',
        '..KBBBBBBK..',
        '.KGBBKKBBGK.',
        '.KGGKKKKGGK.',
        '.KKKKBBKKKK.',
        '.KKKKBBKKKK.',
        '.KGGKKKKGGK.',
        '.KGBBKKBBGK.',
        '..KBBBBBBK..',
        '...KKKKKK...',
        '............',
      ],
      { K: C.k, B: C.blue, G: C.green },
    ),
  },
  settings: {
    pixels: pxFromStr(
      [
        '............',
        '...KK..KK...',
        '..KMMKKMMK..',
        '.KMMMMMMMMK.',
        '.KMMKKKKMMK.',
        'KMMK.KK.KMMK',
        'KMMK.KK.KMMK',
        '.KMMKKKKMMK.',
        '.KMMMMMMMMK.',
        '..KMMKKMMK..',
        '...KK..KK...',
        '............',
      ],
      { K: C.k, M: C.main },
    ),
  },
  book: {
    pixels: pxFromStr(
      [
        '............',
        '.KKKKKKKKKK.',
        '.KOOOOKOOOK.',
        '.KOLLOKOLOK.',
        '.KOLLOKOLOK.',
        '.KOLLOKOLOK.',
        '.KOLLOKOLOK.',
        '.KOLLOKOLOK.',
        '.KOLLOKOLOK.',
        '.KOOOOKOOOK.',
        '.KKKKKKKKKK.',
        '............',
      ],
      { K: C.k, O: C.brown, L: C.l },
    ),
  },
  dna: {
    pixels: pxFromStr(
      [
        '............',
        '.KKK....KKK.',
        'KCCCKKKKCCCK',
        '.KCCCCCCCCK.',
        '..KCCCCCCK..',
        '...KCCCCK...',
        '...KCCCCK...',
        '..KCCCCCCK..',
        '.KCCCCCCCCK.',
        'KCCCKKKKCCCK',
        '.KKK....KKK.',
        '............',
      ],
      { K: C.k, C: C.cyan },
    ),
  },
  tree: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '...KGGGGK...',
        '..KGGGGGGK..',
        '.KGGGGGGGGK.',
        '.KGLLGGLGGK.',
        '..KGGGGGGK..',
        '...KKGGKK...',
        '....KOOK....',
        '....KOOK....',
        '...KOOOOK...',
        '....KKKK....',
      ],
      { K: C.k, G: C.greenDark, L: C.green, O: C.brown },
    ),
  },
  reset: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '..KKMMMMKK..',
        '.KMMK..KMMK.',
        'KMMK....KKK.',
        'KMK........K',
        'KMK........K',
        'KMMK....KMMK',
        '.KMMK..KMMK.',
        '..KKMMMMKK..',
        '....KKKK....',
        '............',
      ],
      { K: C.k, M: C.main },
    ),
  },

  // ===== MUNDO/EXPEDIÇÃO =====
  house: {
    pixels: pxFromStr(
      [
        '.....KK.....',
        '....KOOK....',
        '...KOOOOK...',
        '..KOOOOOOK..',
        '.KOOOOOOOOK.',
        'KOOOOOOOOOOK',
        'K.KKKKKKKK.K',
        'K.KKWWKWWKK.',
        'K.KKWWKWWKK.',
        'K.KKKKKKKK.K',
        'KKKKKKKKKKKK',
        '............',
      ],
      { K: C.k, O: C.red, W: C.cyan },
    ),
  },
  world: {
    pixels: pxFromStr(
      [
        '............',
        '...KKKKKK...',
        '..KGGGGGGK..',
        '.KGGBBBBGGK.',
        '.KGBBBBBBGK.',
        '.KGBBBBBBGK.',
        '.KGGBBBBGGK.',
        '.KGGGBBGGGK.',
        '..KGGGGGGK..',
        '...KKKKKK...',
        '............',
        '............',
      ],
      { K: C.k, G: C.green, B: C.blue },
    ),
  },
  sword: {
    pixels: pxFromStr(
      [
        '..........KK',
        '.........KLK',
        '........KLLK',
        '.......KLLK.',
        '......KLLK..',
        '.....KLLK...',
        '....KLLK....',
        '...KLLK.....',
        '.KKKKK......',
        'KOOKKKK.....',
        '.KKKK.......',
        '............',
      ],
      { K: C.k, L: C.l, O: C.brown },
    ),
  },
  shield: {
    pixels: pxFromStr(
      [
        '............',
        '..KKKKKKKK..',
        '.KCCCCCCCCK.',
        'KCCCCCCCCCCK',
        'KCKKKKKKKKCK',
        'KCKLLLLLLKCK',
        'KCKLLKKLLKCK',
        'KCKLLLLLLKCK',
        'KCKKKKKKKKCK',
        '.KCCCCCCCCK.',
        '..KCCCCCCK..',
        '...KKKKKK...',
      ],
      { K: C.k, C: C.cyan, L: C.l },
    ),
  },
  heart: {
    pixels: pxFromStr(
      [
        '............',
        '..KK....KK..',
        '.KRRK..KRRK.',
        'KRRRRKKRRRRK',
        'KRRWRRRRRRRK',
        'KRRWRRRRRRRK',
        '.KRRRRRRRRK.',
        '..KRRRRRRK..',
        '...KRRRRK...',
        '....KRRK....',
        '.....KK.....',
        '............',
      ],
      { K: C.k, R: C.red, W: C.w },
    ),
  },
  inventory: {
    pixels: pxFromStr(
      [
        '............',
        '...KKKKKK...',
        '...KOOOOK...',
        '.KKKKOOKKKK.',
        '.KOOOOOOOOK.',
        '.KOLLOOLLOK.',
        '.KOKKOOKKOK.',
        '.KOLLOOLLOK.',
        '.KOOOOOOOOK.',
        '.KOOOOOOOOK.',
        '.KKKKKKKKKK.',
        '............',
      ],
      { K: C.k, O: C.brown, L: C.l },
    ),
  },

  // ===== ITEMS =====
  berry: {
    pixels: pxFromStr(
      [
        '............',
        '.....KK.....',
        '....KGGK....',
        '...KKGGKK...',
        '..KRRKKRRK..',
        '.KRRRRRRRRK.',
        'KRWRRRRRRRRK',
        'KRWRRRRRRRRK',
        '.KRRRRRRRRK.',
        '..KRRRRRRK..',
        '...KKKKKK...',
        '............',
      ],
      { K: C.k, R: C.red, G: C.greenDark, W: C.w },
    ),
  },
  shell: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '...KOOOOK...',
        '..KOLLOOK...',
        '.KOLLLOOK...',
        '.KOLLLLOOK..',
        '.KOLLLLLOK..',
        '.KKKKKKKKK..',
        '..KOLOLOK...',
        '...KKKKK....',
        '............',
        '............',
      ],
      { K: C.k, O: C.orange, L: C.l },
    ),
  },
  crystal: {
    pixels: pxFromStr(
      [
        '............',
        '.....K......',
        '....KCK.....',
        '...KCWCK....',
        '..KCCWCCK...',
        '.KCCCWCCCK..',
        'KCCCCWCCCCK.',
        'KCCCCWCCCCK.',
        '.KCCCWCCCK..',
        '..KCCWCCK...',
        '...KCWCK....',
        '....KCK.....',
      ],
      { K: C.k, C: C.cyan, W: C.w },
    ),
  },
  flower: {
    pixels: pxFromStr(
      [
        '............',
        '....KPPK....',
        '...KPPPPK...',
        '..KPPPPPPK..',
        '.KPPPYYPPPK.',
        '.KPPYYYYPPK.',
        '.KPPPYYPPPK.',
        '..KPPPPPPK..',
        '...KPPPPK...',
        '....KPPK....',
        '....KGGK....',
        '...KGGGGK...',
      ],
      { K: C.k, P: C.pink, Y: C.yellow, G: C.greenDark },
    ),
  },

  // ===== STATUS =====
  sun: {
    pixels: pxFromStr(
      [
        '.....KK.....',
        '..K..KK..K..',
        '.KK.KYYK.KK.',
        '...KYYYYK...',
        '..KYLLLLYK..',
        'KKYLWWWWLYKK',
        'KKYLWWWWLYKK',
        '..KYLLLLYK..',
        '...KYYYYK...',
        '.KK.KYYK.KK.',
        '..K..KK..K..',
        '.....KK.....',
      ],
      { K: C.k, Y: C.yellow, L: '#fff1a8', W: C.w },
    ),
  },
  moon: {
    pixels: pxFromStr(
      [
        '....KKKKK...',
        '...KLLLLKK..',
        '..KLLLLLLLK.',
        '.KLLLKKKKKK.',
        '.KLLKK......',
        'KLLLK.......',
        'KLLLK.......',
        '.KLLKK......',
        '.KLLLKKKKKK.',
        '..KLLLLLLLK.',
        '...KLLLLKK..',
        '....KKKKK...',
      ],
      { K: C.k, L: '#e8e0d0' },
    ),
  },
  skull: {
    pixels: pxFromStr(
      [
        '............',
        '..KKKKKKKK..',
        '.KLLLLLLLLK.',
        'KLLLLLLLLLLK',
        'KLLKKLLKKLLK',
        'KLKKKLLKKKLK',
        'KLLLLKKLLLLK',
        'KLLKLLLLKLLK',
        '.KKKLLLLKKK.',
        '..KKKKKKKK..',
        '............',
        '............',
      ],
      { K: C.k, L: '#dcd4c0' },
    ),
  },
  sleeping: {
    pixels: pxFromStr(
      [
        '............',
        '....KKKK....',
        '...KZZZZK...',
        '......KK....',
        '.....KK.....',
        '....KZZK....',
        '...KK.......',
        '..KZZ.......',
        '.KK.........',
        'KZ..........',
        'KK..........',
        '............',
      ],
      { K: C.k, Z: '#cccccc' },
    ),
  },
};

export function PixelIcon({ name, size = 24, color = '#ffd6b3', style }: Props) {
  const def = ICONS[name];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      style={{
        imageRendering: 'pixelated',
        shapeRendering: 'crispEdges',
        display: 'block',
        ...style,
      }}
      aria-hidden="true"
    >
      {def.pixels.map(([x, y, c], i) => (
        <rect
          key={i}
          x={x}
          y={y}
          width={1}
          height={1}
          fill={c === '#main' ? color : c}
        />
      ))}
    </svg>
  );
}
